import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const FrontOfficeAvailability = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [availability, setAvailability] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const [frontOfficeSettings, setFrontOfficeSettings] = useState({
    checkInTime: '14:00',
    checkOutTime: '12:00'
  });

  const [showRoomDetailsModal, setShowRoomDetailsModal] = useState(false);
  const [showAssignGuestModal, setShowAssignGuestModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showAssignMaintenanceModal, setShowAssignMaintenanceModal] = useState(false);
  const [showHousekeepingModal, setShowHousekeepingModal] = useState(false);
  const [showAssignHousekeepingModal, setShowAssignHousekeepingModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomDetails, setRoomDetails] = useState(null);
  const [maintenanceDetails, setMaintenanceDetails] = useState(null);
  const [housekeepingDetails, setHousekeepingDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showExtendStayModal, setShowExtendStayModal] = useState(false);
  const [extendingStay, setExtendingStay] = useState(false);
  const [extendStayData, setExtendStayData] = useState({
    newCheckOutDate: '',
    extraDays: 0,
    additionalAmount: 0,
    newTotalAmount: 0
  });

  // Other Charges State
  const [showAddChargeModal, setShowAddChargeModal] = useState(false);
  const [otherCharges, setOtherCharges] = useState([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [newCharge, setNewCharge] = useState({
    chargeType: '',
    description: '',
    amount: ''
  });

  const [assignGuestData, setAssignGuestData] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    guestCountryCode: '+91',
    guestNationality: 'Indian',
    guestGender: '',
    guestState: '',
    guestGstNumber: '',
    bookingType: 'Single',
    numberOfGuests: 1,
    guestIdProof: '',
    checkInDate: new Date().toISOString().split('T')[0],
    checkOutDate: '',
    totalAmount: '',
    includeGst: false,
    gstAmount: 0,
    guest2Name: '',
    guest2Email: '',
    guest2Phone: '',
    guest2IdProof: '',
    familyMembers: [],
    payAdvance: false,
    advanceAmount: '',
    advancePaymentMethod: '',
    customRoomRent: '',
    isComplementary: false
  });

  const addFamilyMember = () => {
    setAssignGuestData({
      ...assignGuestData,
      familyMembers: [...assignGuestData.familyMembers, { name: '', email: '', phone: '', idProof: '' }]
    });
  };

  const removeFamilyMember = (index) => {
    const updatedMembers = assignGuestData.familyMembers.filter((_, i) => i !== index);
    setAssignGuestData({
      ...assignGuestData,
      familyMembers: updatedMembers
    });
  };

  const updateFamilyMember = (index, field, value) => {
    const updatedMembers = assignGuestData.familyMembers.map((member, i) =>
      i === index ? { ...member, [field]: value } : member
    );
    setAssignGuestData({
      ...assignGuestData,
      familyMembers: updatedMembers
    });
  };

  const [maintenanceData, setMaintenanceData] = useState({
    maintenanceType: 'repair',
    description: '',
    priority: 'medium',
    startDate: new Date().toISOString().split('T')[0],
    estimatedEndDate: '',
    estimatedDuration: '',
    assignedTo: '',
    costEstimate: '',
    notes: ''
  });

  const [housekeepingData, setHousekeepingData] = useState({
    priority: 'normal',
    taskType: 'standard_cleaning',
    notes: ''
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    available: '#10b981',
    occupied: '#ef4444',
    maintenance: '#fbbf24',
    housekeeping: '#8b5cf6'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedRoomType, dateRange]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchFrontOfficeSettings(),
      fetchAvailability(),
      fetchForecast()
    ]);
    setLoading(false);
  };

  const fetchFrontOfficeSettings = async () => {
    try {
      const data = await apiGet('/admin/front-office/settings');
      if (data.success) {
        const settings = {};
        data.data.forEach(setting => {
          if (setting.setting_key === 'check_in_time') {
            settings.checkInTime = setting.setting_value;
          } else if (setting.setting_key === 'check_out_time') {
            settings.checkOutTime = setting.setting_value;
          }
        });
        setFrontOfficeSettings(settings);
      }
    } catch (error) {
      console.error('Error fetching front office settings:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      let url = `/admin/front-office/availability/calendar?startDate=${dateRange.start}&endDate=${dateRange.end}`;

      if (selectedRoomType !== 'all') {
        url += `&roomType=${selectedRoomType}`;
      }

      const data = await apiGet(url);
      if (data.success) setAvailability(data.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchForecast = async () => {
    try {
      const data = await apiGet('/admin/front-office/availability/forecast');
      if (data.success) setForecast(data.data);
    } catch (error) {
      console.error('Error fetching forecast:', error);
    }
  };

  const fetchRoomDetails = async (roomId) => {
    setLoadingDetails(true);
    try {
      const data = await apiGet('/admin/stay-overview/room/${roomId}');
      if (data.success) {
        setRoomDetails(data.data);
      }
    } catch (error) {
      console.error('Error fetching room details:', error);
      alert('Failed to load room details');
    }
    setLoadingDetails(false);
  };

  const fetchMaintenanceDetails = async (roomId) => {
    setLoadingDetails(true);
    try {
      const data = await apiGet('/admin/front-office/maintenance/${roomId}');
      if (data.success) {
        setMaintenanceDetails(data.data);
      }
    } catch (error) {
      console.error('Error fetching maintenance details:', error);
      alert('Failed to load maintenance details');
    }
    setLoadingDetails(false);
  };

  const fetchHousekeepingDetails = async (roomId) => {
    setLoadingDetails(true);
    try {
      const data = await apiGet('/admin/front-office/housekeeping/${roomId}');
      if (data.success) {
        setHousekeepingDetails(data.data);
      }
    } catch (error) {
      console.error('Error fetching housekeeping details:', error);
    }
    setLoadingDetails(false);
  };

  const assignHousekeepingAfterCheckout = async (roomId, roomNumber) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/front-office/housekeeping/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomId: roomId,
          priority: 'high',
          taskType: 'standard_cleaning',
          notes: 'Post-checkout cleaning required',
          isAutoAssigned: true
        })
      });

      const data = await response.json();

      if (data.success) {
        console.log(`Housekeeping auto-assigned for room ${roomNumber}`);
      }
    } catch (error) {
      console.error('Error auto-assigning housekeeping:', error);
    }
  };

  const calculateActualAmount = (checkInDate, checkOutDate, pricePerNight) => {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    return nights * pricePerNight;
  };

  const calculateCurrentStayAmount = (checkInDate, pricePerNight) => {
    const checkIn = new Date(checkInDate);
    checkIn.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Calculate nights up to today (checkout day)
    // Example: Check-in 10/01, checkout 12/01 = 2 nights (10-11, 11-12)
    const nightsStayed = Math.max(1, Math.ceil((today - checkIn) / (1000 * 60 * 60 * 24)) + 1);
    return nightsStayed * pricePerNight;
  };

  // ==================== OTHER CHARGES FUNCTIONS ====================

  const fetchOtherCharges = async (bookingId) => {
    if (!bookingId) return;

    setLoadingCharges(true);
    try {
      const data = await apiGet('/admin/other-charges/${bookingId}');

      if (data.success) {
        setOtherCharges(data.charges || []);
      }
    } catch (error) {
      console.error('Error fetching other charges:', error);
      alert('Failed to load other charges');
    } finally {
      setLoadingCharges(false);
    }
  };

  const handleAddCharge = async (e) => {
    e.preventDefault();

    if (!newCharge.chargeType || !newCharge.amount) {
      alert('Please fill in charge type and amount');
      return;
    }

    console.log('Room Details Object:', roomDetails);
    console.log('Trying to use booking ID:', roomDetails?.room?.booking_id);

    if (!roomDetails?.room?.booking_id) {
      alert('No booking found');
      return;
    }

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/other-charges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: roomDetails.room.booking_id,
          chargeType: newCharge.chargeType,
          description: newCharge.description || '',
          amount: parseFloat(newCharge.amount),
          createdBy: 'frontdesk' // You can get this from user session
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Charge added successfully!');
        setNewCharge({ chargeType: '', description: '', amount: '' });
        setShowAddChargeModal(false);
        // Refresh charges list
        await fetchOtherCharges(roomDetails.room.booking_id);
      } else {
        alert(data.message || 'Failed to add charge');
      }
    } catch (error) {
      console.error('Error adding charge:', error);
      alert('Failed to add charge');
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    if (!window.confirm('Are you sure you want to delete this charge?')) {
      return;
    }

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/other-charges/${chargeId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        alert('Charge deleted successfully!');
        // Refresh charges list
        if (roomDetails?.room?.booking_id) {
          await fetchOtherCharges(roomDetails.room.booking_id);
        }
      } else {
        alert(data.message || 'Failed to delete charge');
      }
    } catch (error) {
      console.error('Error deleting charge:', error);
      alert('Failed to delete charge');
    }
  };

  const handleCheckout = async () => {
    console.log('handleCheckout called');
    if (!roomDetails || !roomDetails.room.booking_id) {
      console.log('No room details or booking id');
      alert('No active booking found');
      return;
    }

    console.log('Processing checkout for:', roomDetails.room);

    const checkInDate = new Date(roomDetails.room.check_in_date);
    const plannedCheckOutDate = new Date(roomDetails.room.check_out_date);

    if (isNaN(checkInDate.getTime()) || isNaN(plannedCheckOutDate.getTime())) {
      console.error('Invalid dates:', roomDetails.room.check_in_date, roomDetails.room.check_out_date);
      alert('Error: Booking dates are invalid');
      return;
    }

    const today = new Date();
    const todayDateOnly = new Date(today);
    todayDateOnly.setHours(0, 0, 0, 0);

    const pricePerNight = parseFloat(roomDetails.room.price_per_night || 0);
    const bookedAmount = parseFloat(roomDetails.room.total_amount || 0);
    const totalPaid = parseFloat(roomDetails.totalPaid || 0);

    let actualAmount;
    let isEarlyCheckout = false;
    let isLateCheckout = false;

    // Complementary bookings always have zero cost
    if (roomDetails.room.is_complementary) {
      actualAmount = 0;
    } else {
      // Check if today is the planned checkout date
      const plannedCheckoutDateOnly = new Date(plannedCheckOutDate);
      plannedCheckoutDateOnly.setHours(0, 0, 0, 0);

      if (todayDateOnly < plannedCheckoutDateOnly) {
        // Early checkout (before planned date)
        isEarlyCheckout = true;
        actualAmount = calculateCurrentStayAmount(roomDetails.room.check_in_date, pricePerNight);
      } else if (todayDateOnly.getTime() === plannedCheckoutDateOnly.getTime()) {
        // Checking out on the planned date - check if past checkout time
        const [checkoutHour, checkoutMinute] = frontOfficeSettings.checkOutTime.split(':').map(Number);
        const checkoutTimeToday = new Date(today);
        checkoutTimeToday.setHours(checkoutHour, checkoutMinute, 0, 0);

        if (today > checkoutTimeToday) {
          // Late checkout - past checkout time on checkout date
          isLateCheckout = true;
          const baseAmount = calculateActualAmount(roomDetails.room.check_in_date, roomDetails.room.check_out_date, pricePerNight);
          actualAmount = baseAmount + pricePerNight; // Add extra night charge
        } else {
          // On time checkout
          actualAmount = calculateActualAmount(roomDetails.room.check_in_date, roomDetails.room.check_out_date, pricePerNight);
        }
      } else {
        // Checking out after planned date (overstay)
        actualAmount = calculateCurrentStayAmount(roomDetails.room.check_in_date, pricePerNight);
      }
    }

    // If late checkout detected, update the booking total amount in database
    if (isLateCheckout && actualAmount > bookedAmount) {
      try {
        const response = await fetch(`${PRODUCTION_URL}/api/admin/stay-overview/update-booking-amount/${roomDetails.room.booking_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            newTotalAmount: actualAmount,
            reason: 'Late checkout charge'
          })
        });

        const data = await response.json();

        if (data.success) {
          // Refresh room details to show updated amount
          await fetchRoomDetails(roomDetails.room.id);
          alert(
            `Late Checkout Charge Applied!\n\n` +
            `An extra night charge of ₹${pricePerNight.toLocaleString('en-IN')} has been added to your bill.\n` +
            `New Total: ₹${actualAmount.toLocaleString('en-IN')}\n\n` +
            `Please clear the payment before checkout.`
          );
          setCheckingOut(false);
          return;
        }
      } catch (error) {
        console.error('Error updating booking amount:', error);
      }
    }

    const pendingAmount = actualAmount - totalPaid;
    const refundAmount = isEarlyCheckout ? Math.max(0, totalPaid - actualAmount) : 0;

    // Skip payment validation for complementary bookings
    if (!roomDetails.room.is_complementary && pendingAmount > 0) {
      alert(
        `Please clear the payment first!\n\n` +
        `Actual Stay Amount: ₹${actualAmount.toLocaleString('en-IN')}\n` +
        `Total Paid: ₹${totalPaid.toLocaleString('en-IN')}\n` +
        `Pending Amount: ₹${pendingAmount.toLocaleString('en-IN')}\n\n` +
        `Please collect ₹${pendingAmount.toLocaleString('en-IN')} before checkout.`
      );
      return;
    }

    const nightsStayed = Math.ceil((todayDateOnly - checkInDate) / (1000 * 60 * 60 * 24));
    const nightsBooked = Math.ceil((plannedCheckOutDate - checkInDate) / (1000 * 60 * 60 * 24));

    const confirmMessage = roomDetails.room.is_complementary
      ? `Complementary Room Checkout:\n\n` +
      `Guest: ${roomDetails.room.guest_name}\n` +
      `Room: ${roomDetails.room.room_number}\n` +
      `Total Stay: ${nightsStayed} night(s)\n` +
      `Amount: ₹0 (Complementary)\n\n` +
      `Proceed with checkout?`
      : isEarlyCheckout
        ? `Early Checkout Confirmation:\n\n` +
        `Booked Amount: ₹${bookedAmount.toLocaleString('en-IN')} (${nightsBooked} nights)\n` +
        `Actual Stay: ${Math.max(1, nightsStayed)} night(s)\n` +
        `Actual Amount: ₹${actualAmount.toLocaleString('en-IN')}\n` +
        `Total Paid: ₹${totalPaid.toLocaleString('en-IN')}\n` +
        (refundAmount > 0 ? `Refund Due: ₹${refundAmount.toLocaleString('en-IN')}\n\n` : '\n') +
        `Proceed with early checkout?`
        : isLateCheckout
          ? `Late Checkout Confirmation:\n\n` +
          `⚠️ Checkout time was ${frontOfficeSettings.checkOutTime}\n` +
          `Late checkout charge applied: +1 night\n\n` +
          `Guest: ${roomDetails.room.guest_name}\n` +
          `Room: ${roomDetails.room.room_number}\n` +
          `Total Stay: ${nightsBooked + 1} night(s)\n` +
          `Amount: ₹${actualAmount.toLocaleString('en-IN')}\n` +
          `Paid: ₹${totalPaid.toLocaleString('en-IN')}\n\n` +
          `Proceed with checkout?`
          : `Checkout Confirmation:\n\n` +
          `Guest: ${roomDetails.room.guest_name}\n` +
          `Room: ${roomDetails.room.room_number}\n` +
          `Total Stay: ${nightsStayed} night(s)\n` +
          `Amount: ₹${actualAmount.toLocaleString('en-IN')}\n` +
          `Paid: ₹${totalPaid.toLocaleString('en-IN')}\n\n` +
          `Proceed with checkout?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCheckingOut(true);
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/stay-overview/checkout/${roomDetails.room.booking_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          actualAmount: actualAmount,
          isEarlyCheckout: isEarlyCheckout
        })
      });

      const data = await response.json();

      if (data.success) {
        await assignHousekeepingAfterCheckout(roomDetails.room.id, roomDetails.room.room_number);

        alert(
          `Checkout Successful!\n\n` +
          `Room ${roomDetails.room.room_number} is now available.\n` +
          `Guest: ${roomDetails.room.guest_name}\n` +
          `Stay Duration: ${Math.max(1, nightsStayed)} night(s)\n` +
          `Final Amount: ₹${actualAmount.toLocaleString('en-IN')}\n\n` +
          `🧹 Housekeeping has been automatically assigned (High Priority)`
        );
        closeModals();
        fetchData();
      } else {
        alert(data.message || 'Checkout failed');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      alert('Failed to checkout guest');
    }
    setCheckingOut(false);
  };

  const handleRoomClick = async (room) => {
    setSelectedRoom(room);

    if (room.status === 'occupied') {
      setShowRoomDetailsModal(true);
      await fetchRoomDetails(room.id);
      // Fetch other charges for this booking
      const roomDetailsData = await apiGet('/admin/rooms/${room.id}/details');
      if (roomDetailsData.success && roomDetailsData.room?.booking_id) {
        await fetchOtherCharges(roomDetailsData.room.booking_id);
      }
    } else if (room.status === 'available') {
      if (room.housekeeping_status === 'dirty') {
        setShowHousekeepingModal(true);
        await fetchHousekeepingDetails(room.id);
      } else {
        setAssignGuestData({
          guestName: '',
          guestEmail: '',
          guestPhone: '',
          guestCountryCode: '+91',
          guestNationality: 'Indian',
          bookingType: 'Single',
          numberOfGuests: 1,
          guestIdProof: '',
          checkInDate: new Date().toISOString().split('T')[0],
          checkOutDate: '',
          totalAmount: room.price_per_night || '',
          guest2Name: '',
          guest2Email: '',
          guest2Phone: '',
          guest2IdProof: '',
          familyMembers: []
        });
        setShowAssignGuestModal(true);
      }
    } else if (room.status === 'maintenance') {
      setShowMaintenanceModal(true);
      await fetchMaintenanceDetails(room.id);
    }
  };

  const handleAssignMaintenance = (room) => {
    setSelectedRoom(room);
    setMaintenanceData({
      maintenanceType: 'repair',
      description: '',
      priority: 'medium',
      startDate: new Date().toISOString().split('T')[0],
      estimatedEndDate: '',
      estimatedDuration: '',
      assignedTo: '',
      costEstimate: '',
      notes: ''
    });
    setShowAssignMaintenanceModal(true);
  };

  const handleAssignHousekeeping = (room) => {
    setSelectedRoom(room);
    setHousekeepingData({
      priority: 'normal',
      taskType: 'standard_cleaning',
      notes: ''
    });
    setShowAssignHousekeepingModal(true);
  };

  const handleAssignGuest = async (e) => {
    e.preventDefault();

    // Skip validation for complementary bookings
    if (!assignGuestData.isComplementary) {
      if (!assignGuestData.guestName || !assignGuestData.guestPhone || !assignGuestData.checkOutDate) {
        alert('Please fill all required fields');
        return;
      }
    }

    // Calculate total amount
    let calculatedTotal = 0;
    let gstAmount = 0;
    if (!assignGuestData.isComplementary && assignGuestData.checkInDate && assignGuestData.checkOutDate) {
      const checkIn = new Date(assignGuestData.checkInDate);
      const checkOut = new Date(assignGuestData.checkOutDate);
      const daysDiff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      const billingDays = daysDiff === 0 ? 1 : daysDiff;
      const dailyRate = assignGuestData.customRoomRent || selectedRoom.price_per_night || 0;
      const baseAmount = billingDays * parseFloat(dailyRate);

      // Calculate GST if enabled
      if (assignGuestData.includeGst) {
        gstAmount = baseAmount * 0.05;
      }
      calculatedTotal = baseAmount + gstAmount;
    }

    try {
      const data = await apiPost('/admin/stay-overview/assign-guest', {
        roomId: selectedRoom.id,
        isComplementary: assignGuestData.isComplementary,
        customRoomRent: assignGuestData.customRoomRent ? parseFloat(assignGuestData.customRoomRent) : null,
        guestName: assignGuestData.isComplementary ? 'Complementary' : assignGuestData.guestName,
        guestEmail: assignGuestData.guestEmail,
        guestPhone: assignGuestData.guestPhone,
        guestNationality: assignGuestData.guestNationality,
        guestGender: assignGuestData.guestGender,
        guestState: assignGuestData.guestState,
        guestGstNumber: assignGuestData.guestGstNumber,
        bookingType: assignGuestData.bookingType,
        numberOfGuests: assignGuestData.numberOfGuests,
        guestIdProof: assignGuestData.guestIdProof,
        checkInDate: assignGuestData.checkInDate,
        checkOutDate: assignGuestData.checkOutDate,
        totalAmount: calculatedTotal,
        includeGst: assignGuestData.includeGst,
        gstAmount: gstAmount,
        guest2Name: assignGuestData.guest2Name,
        guest2Email: assignGuestData.guest2Email,
        guest2Phone: assignGuestData.guest2Phone,
        guest2IdProof: assignGuestData.guest2IdProof,
        familyMembers: assignGuestData.familyMembers,
        payAdvance: assignGuestData.payAdvance,
        advanceAmount: assignGuestData.payAdvance ? parseFloat(assignGuestData.advanceAmount) : 0,
        advancePaymentMethod: assignGuestData.payAdvance ? assignGuestData.advancePaymentMethod : null
      });

      if (data.success) {
        let successMessage = '';

        if (assignGuestData.isComplementary) {
          successMessage = `Complementary room assigned successfully!\nRoom ${selectedRoom.room_number} is now occupied.`;
        } else {
          const sameDayInfo = data.data.sameDayCheckout ? '\n⚠️ Same-day checkout - 1 day charged' : '';
          const customRentInfo = assignGuestData.customRoomRent ? `\n💰 Custom rent: ₹${assignGuestData.customRoomRent}/day` : '';

          successMessage = assignGuestData.payAdvance
            ? `Guest assigned successfully!\nBooking ID: ${data.data.bookingId}${sameDayInfo}${customRentInfo}\n\n✓ Advance payment of ₹${assignGuestData.advanceAmount} received via ${assignGuestData.advancePaymentMethod}`
            : `Guest assigned successfully!\nBooking ID: ${data.data.bookingId}${sameDayInfo}${customRentInfo}`;
        }

        alert(successMessage);
        setShowAssignGuestModal(false);
        fetchData();
      } else {
        alert(data.message || 'Failed to assign guest');
      }
    } catch (error) {
      console.error('Error assigning guest:', error);
      alert('Failed to assign guest');
    }
  };

  const handleSubmitMaintenance = async (e) => {
    e.preventDefault();

    if (!maintenanceData.description || !maintenanceData.estimatedEndDate) {
      alert('Please fill all required fields');
      return;
    }

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/front-office/maintenance/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomId: selectedRoom.id,
          maintenanceType: maintenanceData.maintenanceType,
          description: maintenanceData.description,
          priority: maintenanceData.priority,
          startDate: maintenanceData.startDate,
          estimatedEndDate: maintenanceData.estimatedEndDate,
          estimatedDuration: maintenanceData.estimatedDuration ? parseInt(maintenanceData.estimatedDuration) : null,
          assignedTo: maintenanceData.assignedTo,
          costEstimate: maintenanceData.costEstimate ? parseFloat(maintenanceData.costEstimate) : 0,
          notes: maintenanceData.notes
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Maintenance scheduled successfully for Room ${selectedRoom.room_number}!`);
        setShowAssignMaintenanceModal(false);
        fetchData();
      } else {
        alert(data.message || 'Failed to assign maintenance');
      }
    } catch (error) {
      console.error('Error assigning maintenance:', error);
      alert('Failed to assign maintenance');
    }
  };

  const handleSubmitHousekeeping = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/front-office/housekeeping/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomId: selectedRoom.id,
          priority: housekeepingData.priority,
          taskType: housekeepingData.taskType,
          notes: housekeepingData.notes,
          isAutoAssigned: false
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Housekeeping task assigned for Room ${selectedRoom.room_number}!`);
        setShowAssignHousekeepingModal(false);
        fetchData();
      } else {
        alert(data.message || 'Failed to assign housekeeping');
      }
    } catch (error) {
      console.error('Error assigning housekeeping:', error);
      alert('Failed to assign housekeeping');
    }
  };

  const handleCompleteMaintenance = async () => {
    if (!maintenanceDetails || !maintenanceDetails.id) {
      alert('No maintenance record found');
      return;
    }

    if (!window.confirm(`Mark maintenance as completed for Room ${selectedRoom.room_number}?`)) {
      return;
    }

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/front-office/maintenance/complete/${maintenanceDetails.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (data.success) {
        alert(`Maintenance completed! Room ${selectedRoom.room_number} is now available.`);
        closeModals();
        fetchData();
      } else {
        alert(data.message || 'Failed to complete maintenance');
      }
    } catch (error) {
      console.error('Error completing maintenance:', error);
      alert('Failed to complete maintenance');
    }
  };

  const handleCompleteHousekeeping = async () => {
    if (!selectedRoom) {
      alert('No room selected');
      return;
    }

    const cleanedBy = window.prompt('Enter staff member name:') || 'Staff';

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/front-office/housekeeping/complete/${selectedRoom.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cleanedBy: cleanedBy
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Housekeeping completed for Room ${selectedRoom.room_number}!`);
        closeModals();
        fetchData();
      } else {
        alert(data.message || 'Failed to complete housekeeping');
      }
    } catch (error) {
      console.error('Error completing housekeeping:', error);
      alert('Failed to complete housekeeping');
    }
  };

  const handleExtendStayClick = () => {
    if (!roomDetails || !roomDetails.room.check_out_date) return;

    // Default to +1 day
    const currentCheckout = new Date(roomDetails.room.check_out_date);
    const newDate = new Date(currentCheckout);
    newDate.setDate(newDate.getDate() + 1);

    // Handle date formatting carefully to avoid timezone issues
    const newDateStr = newDate.toISOString().split('T')[0];
    const pricePerNight = parseFloat(roomDetails.room.price_per_night || 0);
    const currentTotal = parseFloat(roomDetails.room.total_amount || 0);

    setExtendStayData({
      newCheckOutDate: newDateStr,
      extraDays: 1,
      additionalAmount: pricePerNight,
      newTotalAmount: currentTotal + pricePerNight
    });

    setShowRoomDetailsModal(false); // Hide details modal, but keep data
    setShowExtendStayModal(true);
  };

  const handleExtendStayDateChange = (e) => {
    const newDateStr = e.target.value;
    if (!newDateStr) return;

    const currentCheckout = new Date(roomDetails.room.check_out_date);
    currentCheckout.setHours(0, 0, 0, 0);
    const newDate = new Date(newDateStr);
    newDate.setHours(0, 0, 0, 0);

    // Check if new date is valid (must be after current checkout)
    if (newDate <= currentCheckout) {
      alert('New checkout date must be after the current checkout date');
      return;
    }

    const diffTime = newDate.getTime() - currentCheckout.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    const pricePerNight = parseFloat(roomDetails.room.price_per_night || 0);
    const additional = diffDays * pricePerNight;

    setExtendStayData({
      newCheckOutDate: newDateStr,
      extraDays: diffDays,
      additionalAmount: additional,
      newTotalAmount: parseFloat(roomDetails.room.total_amount) + additional
    });
  };

  const handleConfirmExtension = async () => {
    if (!extendStayData.newCheckOutDate || extendStayData.extraDays <= 0) {
      alert('Please select a valid extension date');
      return;
    }

    setExtendingStay(true);
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/stay-overview/extend-stay/${roomDetails.room.booking_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          newCheckOutDate: extendStayData.newCheckOutDate,
          additionalAmount: extendStayData.additionalAmount
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(
          `Stay Extended Successfully!\n\n` +
          `New Checkout: ${new Date(extendStayData.newCheckOutDate).toLocaleDateString()}\n` +
          `Extra Charges: ₹${extendStayData.additionalAmount.toLocaleString('en-IN')}\n` +
          `New Total: ₹${extendStayData.newTotalAmount.toLocaleString('en-IN')}`
        );
        setShowExtendStayModal(false);
        // Clean up and refresh
        setRoomDetails(null);
        setSelectedRoom(null);
        fetchData();
      } else {
        alert(data.message || 'Failed to extend stay');
      }
    } catch (error) {
      console.error('Error extending stay:', error);
      alert('Failed to extend stay');
    }
    setExtendingStay(false);
  };

  const closeModals = () => {
    setShowRoomDetailsModal(false);
    setShowExtendStayModal(false);
    setShowAssignGuestModal(false);
    setShowMaintenanceModal(false);
    setShowAssignMaintenanceModal(false);
    setShowHousekeepingModal(false);
    setShowAssignHousekeepingModal(false);
    setSelectedRoom(null);
    setRoomDetails(null);
    setMaintenanceDetails(null);
    setHousekeepingDetails(null);
  };

  const getAvailabilityRate = () => {
    if (availability.length === 0) return 0;
    const available = availability.filter(r => r.status === 'available').length;
    return Math.round((available / availability.length) * 100);
  };

  const getRoomStatusColor = (status) => {
    switch (status) {
      case 'available': return colors.available;
      case 'occupied': return colors.occupied;
      case 'maintenance': return colors.maintenance;
      default: return colors.textLight;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium':
      case 'normal': return '#ca8a04';
      case 'low': return '#16a34a';
      default: return colors.textLight;
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'urgent': return '#fee2e2';
      case 'high': return '#ffedd5';
      case 'medium':
      case 'normal': return '#fef3c7';
      case 'low': return '#dcfce7';
      default: return colors.lightGray;
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    statValue: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: '800',
      marginBottom: '8px'
    },
    statLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    filterContainer: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '12px' : '16px',
      flexWrap: 'wrap'
    },
    filterButton: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      background: colors.white,
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    activeFilter: {
      background: colors.primary,
      color: colors.white,
      borderColor: colors.primary
    },
    roomGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
      gap: isMobile ? '10px' : '14px',
      marginTop: '16px'
    },
    roomCard: {
      padding: isMobile ? '14px' : '16px',
      borderRadius: '10px',
      border: '2px solid',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative'
    },
    actionButton: {
      padding: '6px 12px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '8px',
      width: '100%'
    },
    maintenanceButton: {
      background: colors.maintenance,
      color: colors.textDark
    },
    housekeepingButton: {
      background: colors.housekeeping,
      color: colors.white
    },
    housekeepingBadge: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      background: colors.housekeeping,
      color: colors.white,
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '9px',
      fontWeight: '700'
    },
    forecastGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(120px, 1fr))',
      gap: '12px',
      marginTop: '16px'
    },
    forecastCard: {
      padding: isMobile ? '12px' : '14px',
      background: colors.lightGray,
      borderRadius: '8px',
      textAlign: 'center'
    },
    input: {
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      width: '100%'
    },
    textarea: {
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      width: '100%',
      minHeight: '80px',
      resize: 'vertical'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '20px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: '16px',
      width: '100%',
      maxWidth: isMobile ? '100%' : '600px',
      maxHeight: '90vh',
      overflow: 'auto',
      padding: isMobile ? '20px' : '30px'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    modalTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: '700',
      color: colors.textDark
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: colors.textLight,
      padding: '4px 8px'
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: `1px solid ${colors.mediumGray}`
    },
    detailLabel: {
      fontWeight: '600',
      color: colors.textLight,
      fontSize: '14px'
    },
    detailValue: {
      fontWeight: '600',
      color: colors.textDark,
      fontSize: '14px',
      textAlign: 'right'
    },
    sectionDivider: {
      margin: '20px 0',
      borderTop: `2px solid ${colors.mediumGray}`,
      paddingTop: '16px'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontWeight: '600',
      fontSize: '13px',
      color: colors.textDark
    },
    select: {
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      width: '100%',
      background: colors.white
    },
    submitButton: {
      width: '100%',
      padding: '14px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
      color: colors.white,
      border: 'none',
      borderRadius: '10px',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '20px'
    },
    checkoutButton: {
      width: '100%',
      padding: '14px',
      background: `linear-gradient(135deg, ${colors.danger} 0%, #dc2626 100%)`,
      color: colors.white,
      border: 'none',
      borderRadius: '10px',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    completeButton: {
      width: '100%',
      padding: '14px',
      background: `linear-gradient(135deg, ${colors.success} 0%, #16a34a 100%)`,
      color: colors.white,
      border: 'none',
      borderRadius: '10px',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '20px'
    },
    paymentSection: {
      background: colors.lightGray,
      padding: '16px',
      borderRadius: '10px',
      marginTop: '16px'
    },
    paymentRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      fontSize: '14px'
    },
    totalAmount: {
      fontSize: '18px',
      fontWeight: '700',
      color: colors.primary
    },
    pendingAmount: {
      fontSize: '18px',
      fontWeight: '700',
      color: colors.danger
    },
    warningBox: {
      background: '#fef3c7',
      border: `2px solid ${colors.warning}`,
      borderRadius: '8px',
      padding: '12px',
      marginTop: '12px',
      fontSize: '13px',
      color: '#92400e'
    },
    priorityBadge: {
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      display: 'inline-block'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading availability...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Room Availability</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Room status & forecast' : 'Monitor room availability, status updates, and occupancy forecasting'}
        </p>
      </div>

      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ ...styles.statValue, color: colors.primary }}>{availability.length}</div>
          <div style={styles.statLabel}>Total Rooms</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.available}` }}>
          <div style={{ ...styles.statValue, color: colors.available }}>
            {availability.filter(r => r.status === 'available').length}
          </div>
          <div style={styles.statLabel}>Available</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.occupied}` }}>
          <div style={{ ...styles.statValue, color: colors.occupied }}>
            {availability.filter(r => r.status === 'occupied').length}
          </div>
          <div style={styles.statLabel}>Occupied</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.maintenance}` }}>
          <div style={{ ...styles.statValue, color: colors.maintenance }}>
            {availability.filter(r => r.status === 'maintenance').length}
          </div>
          <div style={styles.statLabel}>Maintenance</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ ...styles.statValue, color: colors.accent }}>{getAvailabilityRate()}%</div>
          <div style={styles.statLabel}>Availability Rate</div>
        </div>
      </div>

      <div style={{ ...styles.contentCard, marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🕐</span>
            <div>
              <div style={{ fontSize: '12px', color: colors.textLight, fontWeight: '600' }}>Check-in Time</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: colors.primary }}>
                {frontOfficeSettings.checkInTime}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🕐</span>
            <div>
              <div style={{ fontSize: '12px', color: colors.textLight, fontWeight: '600' }}>Check-out Time</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: colors.danger }}>
                {frontOfficeSettings.checkOutTime}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}><span>📅</span> Room Status Overview</h2>

        <div style={styles.filterContainer}>
          {['all', 'Standard', 'Deluxe', 'Premium', 'Executive', 'Penthouse'].map(type => (
            <button
              key={type}
              onClick={() => setSelectedRoomType(type)}
              style={{
                ...styles.filterButton,
                ...(selectedRoomType === type ? styles.activeFilter : {})
              }}
            >
              {type === 'all' ? 'All Rooms' : type}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ fontSize: '12px', color: colors.textLight, display: 'block', marginBottom: '4px' }}>
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              style={styles.input}
            />
          </div>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ fontSize: '12px', color: colors.textLight, display: 'block', marginBottom: '4px' }}>
              End Date
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              style={styles.input}
            />
          </div>
        </div>

        <div style={styles.roomGrid}>
          {availability.map(room => (
            <div key={room.id} style={{ position: 'relative' }}>
              <div
                onClick={() => handleRoomClick(room)}
                style={{
                  ...styles.roomCard,
                  borderColor: room.housekeeping_status === 'dirty' ? colors.housekeeping : getRoomStatusColor(room.status),
                  background: room.housekeeping_status === 'dirty' ? `${colors.housekeeping}10` : `${getRoomStatusColor(room.status)}10`
                }}
                onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1)')}
              >
                {room.housekeeping_status === 'dirty' && (
                  <div style={styles.housekeepingBadge}>🧹 CLEANING</div>
                )}
                <div style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '700', marginBottom: '4px' }}>
                  {room.room_number}
                </div>
                <div style={{ fontSize: isMobile ? '10px' : '11px', color: colors.textLight, marginBottom: '8px' }}>
                  {room.room_type}
                </div>
                <div style={{
                  fontSize: isMobile ? '10px' : '11px',
                  fontWeight: '600',
                  color: room.housekeeping_status === 'dirty' ? colors.housekeeping : getRoomStatusColor(room.status),
                  textTransform: 'uppercase',
                  lineHeight: '1.4'
                }}>
                  {room.housekeeping_status === 'dirty'
                    ? 'AVAILABLE - CLEANING'
                    : room.status === 'occupied' && room.check_out_date
                      ? `OCCUPIED - ${new Date(room.check_out_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`
                      : room.status === 'occupied'
                        ? 'OCCUPIED'
                        : room.status}
                </div>
              </div>
              {room.status === 'available' && (
                <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssignMaintenance(room);
                    }}
                    style={{ ...styles.actionButton, ...styles.maintenanceButton, flex: 1 }}
                  >
                    🔧 Maint.
                  </button>
                  {room.housekeeping_status !== 'dirty' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAssignHousekeeping(room);
                      }}
                      style={{ ...styles.actionButton, ...styles.housekeepingButton, flex: 1 }}
                    >
                      🧹 Clean
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}><span>📊</span> 30-Day Forecast</h2>

        {forecast.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No forecast data available
          </div>
        ) : (
          <div style={styles.forecastGrid}>
            {forecast.slice(0, isMobile ? 10 : 30).map((day, index) => (
              <div key={index} style={styles.forecastCard}>
                <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '4px' }}>
                  {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
                <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '700', color: colors.primary, marginBottom: '4px' }}>
                  {day.expected_arrivals}
                </div>
                <div style={{ fontSize: '10px', color: colors.textLight }}>
                  Arrivals
                </div>
                <div style={{ fontSize: '11px', color: colors.textDark, marginTop: '4px' }}>
                  {day.expected_guests} guests
                </div>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Room Details Modal */}
      {showRoomDetailsModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Room {selectedRoom?.room_number} Details</h2>
              <button style={styles.closeButton} onClick={closeModals}>×</button>
            </div>

            {loadingDetails ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
                <div>Loading details...</div>
              </div>
            ) : roomDetails ? (
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                  Room Information
                </h3>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Room Number</span>
                  <span style={styles.detailValue}>{roomDetails.room?.room_number}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Room Type</span>
                  <span style={styles.detailValue}>{roomDetails.room?.room_type}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Floor</span>
                  <span style={styles.detailValue}>{roomDetails.room?.floor}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Price per Night</span>
                  <span style={styles.detailValue}>₹{parseFloat(roomDetails.room?.price_per_night || 0).toLocaleString('en-IN')}</span>
                </div>

                <div style={styles.sectionDivider}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                    Guest Information
                  </h3>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Guest Name</span>
                    <span style={styles.detailValue}>{roomDetails.room?.guest_name || 'N/A'}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Phone Number</span>
                    <span style={styles.detailValue}>{roomDetails.room?.guest_phone || 'N/A'}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Email</span>
                    <span style={styles.detailValue}>{roomDetails.room?.guest_email || 'N/A'}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Nationality</span>
                    <span style={styles.detailValue}>{roomDetails.room?.guest_nationality || 'N/A'}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Booking Type</span>
                    <span style={styles.detailValue}>{roomDetails.room?.booking_type || 'N/A'}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Number of Guests</span>
                    <span style={styles.detailValue}>{roomDetails.room?.number_of_guests || 'N/A'}</span>
                  </div>
                </div>

                <div style={styles.sectionDivider}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                    Booking Information
                  </h3>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Check-in Date</span>
                    <span style={styles.detailValue}>
                      {roomDetails.room?.check_in_date ? new Date(roomDetails.room.check_in_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Check-in Time</span>
                    <span style={styles.detailValue}>{frontOfficeSettings.checkInTime}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Check-out Date</span>
                    <span style={styles.detailValue}>
                      {roomDetails.room?.check_out_date ? new Date(roomDetails.room.check_out_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Check-out Time</span>
                    <span style={styles.detailValue}>{frontOfficeSettings.checkOutTime}</span>
                  </div>

                  {roomDetails.room?.check_in_date && roomDetails.room?.check_out_date && (
                    (() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const plannedCheckout = new Date(roomDetails.room.check_out_date);
                      plannedCheckout.setHours(0, 0, 0, 0);

                      if (today < plannedCheckout) {
                        const checkIn = new Date(roomDetails.room.check_in_date);
                        checkIn.setHours(0, 0, 0, 0);
                        const nightsStayed = Math.max(1, Math.ceil((today - checkIn) / (1000 * 60 * 60 * 24)));
                        const plannedNights = Math.max(1, Math.ceil((plannedCheckout - checkIn) / (1000 * 60 * 60 * 24)));
                        const actualAmount = nightsStayed * parseFloat(roomDetails.room.price_per_night || 0);

                        return (
                          <div style={styles.warningBox}>
                            <strong>⚠️ Early Checkout Notice:</strong><br />
                            Planned stay: {plannedNights} night(s)<br />
                            Current stay: {nightsStayed} night(s)<br />
                            Actual amount: ₹{actualAmount.toLocaleString('en-IN')}
                          </div>
                        );
                      }
                      return null;
                    })()
                  )}
                </div>

                <div style={styles.sectionDivider}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                    Payment Information
                  </h3>
                  <div style={styles.paymentSection}>
                    <div style={styles.paymentRow}>
                      <span>Booked Amount</span>
                      <span style={{ fontWeight: '600' }}>
                        ₹{parseFloat(roomDetails.room?.total_amount || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    {roomDetails.room?.check_in_date && roomDetails.room?.check_out_date && (
                      (() => {
                        const now = new Date();
                        const todayDateOnly = new Date(now);
                        todayDateOnly.setHours(0, 0, 0, 0);
                        const plannedCheckout = new Date(roomDetails.room.check_out_date);
                        plannedCheckout.setHours(0, 0, 0, 0);
                        const checkIn = new Date(roomDetails.room.check_in_date);

                        // Calculate daily rate - use custom rent if available, otherwise default price
                        const bookedAmount = parseFloat(roomDetails.room?.total_amount || 0);
                        const plannedNights = Math.max(1, Math.ceil((plannedCheckout - checkIn) / (1000 * 60 * 60 * 24)));
                        const dailyRate = bookedAmount / plannedNights; // This includes GST if it was in the booking

                        let actualAmount;
                        let isLateCheckout = false;

                        // Complementary bookings always have zero cost
                        if (roomDetails.room.is_complementary) {
                          actualAmount = 0;
                        } else if (todayDateOnly < plannedCheckout) {
                          // Early checkout - include today as a night
                          const nightsStayed = Math.max(1, Math.ceil((todayDateOnly - checkIn) / (1000 * 60 * 60 * 24)) + 1);
                          actualAmount = nightsStayed * dailyRate;
                        } else if (todayDateOnly.getTime() === plannedCheckout.getTime()) {
                          // On checkout date - check if past checkout time
                          const [checkoutHour, checkoutMinute] = frontOfficeSettings.checkOutTime.split(':').map(Number);
                          const checkoutTimeToday = new Date(now);
                          checkoutTimeToday.setHours(checkoutHour, checkoutMinute, 0, 0);

                          if (now > checkoutTimeToday) {
                            // Late checkout - add extra night
                            isLateCheckout = true;
                            actualAmount = (plannedNights + 1) * dailyRate;
                          } else {
                            // On time - use booked amount
                            actualAmount = bookedAmount;
                          }
                        } else {
                          // Overstay - include today as a night
                          const nightsStayed = Math.max(1, Math.ceil((todayDateOnly - checkIn) / (1000 * 60 * 60 * 24)) + 1);
                          actualAmount = nightsStayed * dailyRate;
                        }

                        // Add other charges to actual amount
                        const totalOtherCharges = otherCharges.reduce((sum, charge) => sum + parseFloat(charge.amount), 0);
                        const actualAmountWithCharges = actualAmount + totalOtherCharges;

                        const totalPaid = parseFloat(roomDetails.totalPaid || 0);
                        const pendingAmount = actualAmountWithCharges - totalPaid;

                        return (
                          <>
                            <div style={styles.paymentRow}>
                              <span>Actual Amount</span>
                              <span style={{ fontWeight: '700', color: colors.primary }}>
                                ₹{actualAmountWithCharges.toLocaleString('en-IN')}
                              </span>
                            </div>
                            {totalOtherCharges > 0 && (
                              <div style={{ fontSize: '11px', color: colors.textLight, marginLeft: '12px', marginTop: '-8px', marginBottom: '8px' }}>
                                (Room: ₹{actualAmount.toLocaleString('en-IN')} + Other Charges: ₹{totalOtherCharges.toLocaleString('en-IN')})
                              </div>
                            )}
                            <div style={styles.paymentRow}>
                              <span>Total Paid</span>
                              <span style={{ fontWeight: '600', color: colors.success }}>
                                ₹{totalPaid.toLocaleString('en-IN')}
                              </span>
                            </div>
                            <div style={{ ...styles.paymentRow, borderTop: `2px solid ${colors.mediumGray}`, paddingTop: '12px', marginTop: '8px' }}>
                              <span style={{ fontWeight: '700' }}>Pending Amount</span>
                              <span style={styles.pendingAmount}>
                                ₹{pendingAmount.toLocaleString('en-IN')}
                              </span>
                            </div>

                            {pendingAmount > 0 && (
                              <div style={{ ...styles.warningBox, marginTop: '12px' }}>
                                <strong>💰 Payment Pending:</strong><br />
                                Please collect ₹{pendingAmount.toLocaleString('en-IN')} before checkout.
                              </div>
                            )}

                            {isLateCheckout && (
                              <div style={{ ...styles.warningBox, marginTop: '12px', background: '#fef3c7', borderColor: '#f59e0b' }}>
                                <strong>⚠️ Late Checkout Charge:</strong><br />
                                Checkout time was {frontOfficeSettings.checkOutTime}. An extra night charge has been added.
                              </div>
                            )}
                          </>
                        );
                      })()
                    )}
                  </div>
                </div>

                {roomDetails.payments && roomDetails.payments.length > 0 && (
                  <div style={styles.sectionDivider}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                      Payment History
                    </h3>
                    {roomDetails.payments.map((payment, index) => (
                      <div key={payment.id} style={{
                        ...styles.detailRow,
                        background: index % 2 === 0 ? colors.lightGray : 'transparent',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        margin: '4px 0'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '13px' }}>
                            ₹{parseFloat(payment.amount).toLocaleString('en-IN')}
                          </div>
                          <div style={{ fontSize: '11px', color: colors.textLight }}>
                            {payment.payment_method} • {new Date(payment.payment_date).toLocaleDateString()}
                          </div>
                        </div>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: payment.payment_status === 'completed' ? colors.success : colors.warning,
                          textTransform: 'uppercase'
                        }}>
                          {payment.payment_status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Other Charges Section */}
                <div style={styles.sectionDivider}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.primary }}>
                      Other Charges
                    </h3>
                    <button
                      onClick={() => setShowAddChargeModal(true)}
                      style={{
                        background: colors.success,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      + Add Charge
                    </button>
                  </div>

                  {loadingCharges ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: colors.textLight }}>
                      Loading charges...
                    </div>
                  ) : otherCharges.length > 0 ? (
                    <>
                      {otherCharges.map((charge, index) => (
                        <div key={charge.id} style={{
                          ...styles.detailRow,
                          background: index % 2 === 0 ? colors.lightGray : 'transparent',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          margin: '4px 0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', textTransform: 'capitalize' }}>
                              {charge.charge_type.replace(/_/g, ' ')}
                            </div>
                            {charge.description && (
                              <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '2px' }}>
                                {charge.description}
                              </div>
                            )}
                            <div style={{ fontSize: '10px', color: colors.textLight, marginTop: '2px' }}>
                              {new Date(charge.created_at).toLocaleString()}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontWeight: '700', fontSize: '14px', color: colors.primary }}>
                              ₹{parseFloat(charge.amount).toLocaleString('en-IN')}
                            </span>
                            <button
                              onClick={() => handleDeleteCharge(charge.id)}
                              style={{
                                background: colors.danger,
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                fontWeight: '600'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                      <div style={{ ...styles.detailRow, borderTop: `2px solid ${colors.mediumGray}`, paddingTop: '10px', marginTop: '10px', fontWeight: '700' }}>
                        <span>Total Other Charges</span>
                        <span style={{ color: colors.primary }}>
                          ₹{otherCharges.reduce((sum, charge) => sum + parseFloat(charge.amount), 0).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: colors.textLight, fontSize: '13px' }}>
                      No other charges added yet
                    </div>
                  )}
                </div>

                <button
                  onClick={handleExtendStayClick}
                  style={{
                    ...styles.submitButton,
                    marginBottom: '10px',
                    background: '#6366f1'
                  }}
                >
                  📅 + Extend Stay
                </button>

                <button
                  type="button"
                  onClick={(e) => {
                    console.log('Checkout button clicked');
                    e.preventDefault();
                    e.stopPropagation();
                    handleCheckout();
                  }}
                  disabled={checkingOut}
                  style={{
                    ...styles.checkoutButton,
                    opacity: checkingOut ? 0.6 : 1,
                    cursor: checkingOut ? 'not-allowed' : 'pointer'
                  }}
                >
                  {checkingOut ? (
                    <>
                      <span>⏳</span>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>🚪</span>
                      <span>Checkout Guest</span>
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
                No details available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign Guest Modal */}
      {showAssignGuestModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Assign Guest to Room {selectedRoom?.room_number}</h2>
              <button style={styles.closeButton} onClick={closeModals}>×</button>
            </div>

            <form onSubmit={handleAssignGuest}>
              {/* Complementary Room Checkbox */}
              <div style={{
                ...styles.formGroup,
                background: colors.lightGray,
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={assignGuestData.isComplementary}
                    onChange={(e) => {
                      const isComp = e.target.checked;
                      const today = new Date().toISOString().split('T')[0];
                      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];

                      setAssignGuestData({
                        ...assignGuestData,
                        isComplementary: isComp,
                        customRoomRent: isComp ? '' : assignGuestData.customRoomRent,
                        totalAmount: isComp ? '0' : assignGuestData.totalAmount,
                        // Set default dates for complementary to avoid validation issues
                        checkInDate: isComp ? today : assignGuestData.checkInDate,
                        checkOutDate: isComp ? tomorrow : assignGuestData.checkOutDate
                      });
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>
                    🎁 Complementary Room (No guest details or billing required)
                  </span>
                </label>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Guest Name {!assignGuestData.isComplementary && '*'}</label>
                <input
                  type="text"
                  style={styles.input}
                  value={assignGuestData.guestName}
                  onChange={(e) => setAssignGuestData({ ...assignGuestData, guestName: e.target.value })}
                  required={!assignGuestData.isComplementary}
                  disabled={assignGuestData.isComplementary}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number {!assignGuestData.isComplementary && '*'}</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <select
                    style={{
                      ...styles.select,
                      width: '140px',
                      minWidth: '140px'
                    }}
                    value={assignGuestData.guestCountryCode}
                    onChange={(e) => {
                      setAssignGuestData({
                        ...assignGuestData,
                        guestCountryCode: e.target.value,
                        // Clear phone validation error if switching countries might fix length (optional, kept simple)
                        guestPhone: assignGuestData.guestPhone // Keep existing number
                      });
                    }}
                  >
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+966">🇸🇦 +966</option>
                  </select>
                  {(() => {
                    const limit = assignGuestData.guestCountryCode === '+91' ? 10 :
                      assignGuestData.guestCountryCode === '+1' ? 10 :
                        assignGuestData.guestCountryCode === '+44' ? 10 : 15;

                    return (
                      <input
                        type="tel"
                        style={{ ...styles.input, flex: 1 }}
                        value={assignGuestData.guestPhone}
                        placeholder="Enter phone number"
                        maxLength={limit}
                        onChange={(e) => {
                          // Allow digits only
                          const cleanValue = e.target.value.replace(/\D/g, '');
                          setAssignGuestData({
                            ...assignGuestData,
                            guestPhone: cleanValue.slice(0, limit)
                          });
                        }}
                        required={!assignGuestData.isComplementary}
                        disabled={assignGuestData.isComplementary}
                      />
                    );
                  })()}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  style={styles.input}
                  value={assignGuestData.guestEmail}
                  onChange={(e) => setAssignGuestData({ ...assignGuestData, guestEmail: e.target.value })}
                  disabled={assignGuestData.isComplementary}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nationality</label>
                  <select
                    style={styles.select}
                    value={assignGuestData.guestNationality}
                    onChange={(e) => setAssignGuestData({ ...assignGuestData, guestNationality: e.target.value })}
                    disabled={assignGuestData.isComplementary}
                  >
                    <option value="Indian">Indian</option>
                    <option value="Foreign">Foreign</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Gender</label>
                  <select
                    style={styles.select}
                    value={assignGuestData.guestGender}
                    onChange={(e) => setAssignGuestData({ ...assignGuestData, guestGender: e.target.value })}
                    disabled={assignGuestData.isComplementary}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Booking Type</label>
                  <select
                    style={styles.select}
                    value={assignGuestData.bookingType}
                    onChange={(e) => setAssignGuestData({ ...assignGuestData, bookingType: e.target.value })}
                    disabled={assignGuestData.isComplementary}
                  >
                    <option value="Single">Single</option>
                    <option value="Couple">Couple</option>
                    <option value="Unmarried Couple">Unmarried Couple</option>
                    <option value="Family/Friends">Family/Friends</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>State/Province</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={assignGuestData.guestState}
                    onChange={(e) => setAssignGuestData({ ...assignGuestData, guestState: e.target.value })}
                    placeholder="Enter state or province"
                    disabled={assignGuestData.isComplementary}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>GST Number (Optional)</label>
                <input
                  type="text"
                  style={styles.input}
                  value={assignGuestData.guestGstNumber}
                  onChange={(e) => setAssignGuestData({ ...assignGuestData, guestGstNumber: e.target.value })}
                  placeholder="Enter GST registration number"
                  disabled={assignGuestData.isComplementary}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Number of Guests</label>
                <input
                  type="number"
                  style={styles.input}
                  value={assignGuestData.numberOfGuests}
                  onChange={(e) => setAssignGuestData({ ...assignGuestData, numberOfGuests: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="10"
                  disabled={assignGuestData.isComplementary}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>ID Proof</label>
                <input
                  type="text"
                  style={styles.input}
                  value={assignGuestData.guestIdProof}
                  onChange={(e) => setAssignGuestData({ ...assignGuestData, guestIdProof: e.target.value })}
                  disabled={assignGuestData.isComplementary}
                />
              </div>

              {(assignGuestData.bookingType === 'Couple' || assignGuestData.bookingType === 'Unmarried Couple') && (
                <div style={styles.sectionDivider}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                    Second Guest Details
                  </h3>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Second Guest Name</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={assignGuestData.guest2Name}
                      onChange={(e) => setAssignGuestData({ ...assignGuestData, guest2Name: e.target.value })}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Second Guest Phone</label>
                    <input
                      type="tel"
                      style={styles.input}
                      value={assignGuestData.guest2Phone}
                      onChange={(e) => setAssignGuestData({ ...assignGuestData, guest2Phone: e.target.value })}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Second Guest Email</label>
                    <input
                      type="email"
                      style={styles.input}
                      value={assignGuestData.guest2Email}
                      onChange={(e) => setAssignGuestData({ ...assignGuestData, guest2Email: e.target.value })}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Second Guest ID Proof</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={assignGuestData.guest2IdProof}
                      onChange={(e) => setAssignGuestData({ ...assignGuestData, guest2IdProof: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {assignGuestData.bookingType === 'Family/Friends' && (
                <div style={styles.sectionDivider}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                    Family Members
                  </h3>
                  {assignGuestData.familyMembers && assignGuestData.familyMembers.map((member, index) => (
                    <div key={index} style={{ ...styles.formGroup, border: `1px solid ${colors.mediumGray}`, padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '600' }}>Member {index + 1}</span>
                        <button type="button" onClick={() => removeFamilyMember(index)} style={{ background: colors.danger, color: colors.white, border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}>Remove</button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Name"
                          style={styles.input}
                          value={member.name}
                          onChange={(e) => updateFamilyMember(index, 'name', e.target.value)}
                        />
                        <input
                          type="email"
                          placeholder="Email"
                          style={styles.input}
                          value={member.email}
                          onChange={(e) => updateFamilyMember(index, 'email', e.target.value)}
                        />
                        <input
                          type="tel"
                          placeholder="Phone"
                          style={styles.input}
                          value={member.phone}
                          onChange={(e) => updateFamilyMember(index, 'phone', e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="ID Proof"
                          style={styles.input}
                          value={member.idProof}
                          onChange={(e) => updateFamilyMember(index, 'idProof', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={addFamilyMember} style={{ ...styles.submitButton, background: colors.accent, marginBottom: '16px' }}>Add Family Member</button>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Check-in Date *</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={assignGuestData.checkInDate}
                    onChange={(e) => setAssignGuestData({ ...assignGuestData, checkInDate: e.target.value })}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Check-out Date *</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={assignGuestData.checkOutDate}
                    onChange={(e) => setAssignGuestData({ ...assignGuestData, checkOutDate: e.target.value })}
                    min={assignGuestData.checkInDate}
                    required={!assignGuestData.isComplementary}
                  />
                </div>
              </div>

              {/* Custom Room Rent */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Custom Room Rent Per Day (Optional)</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder={`Default: ₹${selectedRoom?.price_per_night || 0}`}
                  value={assignGuestData.customRoomRent}
                  onChange={(e) => setAssignGuestData({ ...assignGuestData, customRoomRent: e.target.value })}
                  min="0"
                  step="0.01"
                  disabled={assignGuestData.isComplementary}
                />
                <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '4px' }}>
                  Leave empty to use default room rent (₹{selectedRoom?.price_per_night || 0}/day)
                </div>
              </div>

              {/* GST Checkbox */}
              {!assignGuestData.isComplementary && (
                <div style={{
                  ...styles.formGroup,
                  background: colors.lightGray,
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={assignGuestData.includeGst}
                      onChange={(e) => {
                        setAssignGuestData({
                          ...assignGuestData,
                          includeGst: e.target.checked
                        });
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>
                      📄 Include GST (5% will be added to base amount)
                    </span>
                  </label>
                </div>
              )}

              {/* Total Amount (Auto-calculated) */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Total Amount</label>
                <div style={{
                  ...styles.input,
                  background: colors.lightGray,
                  fontWeight: '700',
                  fontSize: '18px',
                  color: assignGuestData.isComplementary ? colors.success : colors.primary
                }}>
                  {(() => {
                    if (assignGuestData.isComplementary) return '₹0 (Complementary)';

                    // If no dates selected, show message
                    if (!assignGuestData.checkInDate || !assignGuestData.checkOutDate) {
                      // If custom rent is entered, show estimated calculation for 1 day
                      if (assignGuestData.customRoomRent) {
                        const dailyRate = parseFloat(assignGuestData.customRoomRent);
                        const gstAmount = assignGuestData.includeGst ? dailyRate * 0.05 : 0;
                        const totalWithGst = dailyRate + gstAmount;
                        return (
                          <div>
                            <div>₹{totalWithGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })} (1 day estimate)</div>
                            {assignGuestData.includeGst && (
                              <div style={{ fontSize: '12px', color: colors.textLight, marginTop: '4px' }}>
                                Base: ₹{dailyRate.toLocaleString('en-IN')} + GST (5%): ₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </div>
                            )}
                            <div style={{ fontSize: '11px', color: colors.warning, marginTop: '4px' }}>
                              ⚠️ Select checkout date for final amount
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div>
                          <div>₹0</div>
                          <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '4px' }}>
                            Select check-in and check-out dates
                          </div>
                        </div>
                      );
                    }

                    const checkIn = new Date(assignGuestData.checkInDate);
                    const checkOut = new Date(assignGuestData.checkOutDate);
                    const daysDiff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                    const billingDays = daysDiff === 0 ? 1 : daysDiff;
                    const dailyRate = assignGuestData.customRoomRent || selectedRoom?.price_per_night || 0;
                    const baseAmount = billingDays * parseFloat(dailyRate);

                    // Calculate GST if enabled
                    const gstAmount = assignGuestData.includeGst ? baseAmount * 0.05 : 0;
                    const totalWithGst = baseAmount + gstAmount;

                    return (
                      <div>
                        <div>₹{totalWithGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })} ({billingDays} {billingDays === 1 ? 'day' : 'days'})</div>
                        {assignGuestData.includeGst && (
                          <div style={{ fontSize: '12px', color: colors.textLight, marginTop: '4px' }}>
                            Base: ₹{baseAmount.toLocaleString('en-IN')} + GST (5%): ₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {(() => {
                  if (assignGuestData.checkInDate && assignGuestData.checkOutDate) {
                    const checkIn = new Date(assignGuestData.checkInDate);
                    const checkOut = new Date(assignGuestData.checkOutDate);
                    const daysDiff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                    if (daysDiff === 0 && !assignGuestData.isComplementary) {
                      return (
                        <div style={{
                          fontSize: '12px',
                          color: colors.warning,
                          marginTop: '4px',
                          padding: '8px',
                          background: 'rgba(245, 158, 11, 0.1)',
                          borderRadius: '6px',
                          fontWeight: '600'
                        }}>
                          ⚠️ Same-day checkout - Charging for 1 full day
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
              </div>

              <button type="submit" style={styles.submitButton}>
                Assign Guest to Room
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Maintenance Details Modal */}
      {showMaintenanceModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Room {selectedRoom?.room_number} - Maintenance</h2>
              <button style={styles.closeButton} onClick={closeModals}>×</button>
            </div>

            {loadingDetails ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
                <div>Loading...</div>
              </div>
            ) : maintenanceDetails ? (
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                  Maintenance Information
                </h3>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Type</span>
                  <span style={styles.detailValue}>{maintenanceDetails.maintenance_type}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Priority</span>
                  <span style={{
                    ...styles.priorityBadge,
                    background: getPriorityBg(maintenanceDetails.priority),
                    color: getPriorityColor(maintenanceDetails.priority)
                  }}>
                    {maintenanceDetails.priority}
                  </span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Status</span>
                  <span style={styles.detailValue}>{maintenanceDetails.status}</span>
                </div>

                <div style={styles.sectionDivider}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                    Schedule
                  </h3>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Start Date</span>
                    <span style={styles.detailValue}>
                      {new Date(maintenanceDetails.start_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Estimated End</span>
                    <span style={styles.detailValue}>
                      {new Date(maintenanceDetails.estimated_end_date).toLocaleDateString()}
                    </span>
                  </div>
                  {maintenanceDetails.estimated_duration && (
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Duration</span>
                      <span style={styles.detailValue}>{maintenanceDetails.estimated_duration} hours</span>
                    </div>
                  )}
                </div>

                <div style={styles.sectionDivider}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                    Assignment & Cost
                  </h3>
                  {maintenanceDetails.assigned_to && (
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Assigned To</span>
                      <span style={styles.detailValue}>{maintenanceDetails.assigned_to}</span>
                    </div>
                  )}
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Cost Estimate</span>
                    <span style={styles.detailValue}>
                      ₹{parseFloat(maintenanceDetails.cost_estimate || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>

                {maintenanceDetails.description && (
                  <div style={styles.sectionDivider}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                      Description
                    </h3>
                    <p style={{ fontSize: '14px', color: colors.textDark }}>
                      {maintenanceDetails.description}
                    </p>
                  </div>
                )}

                {(maintenanceDetails.status === 'scheduled' || maintenanceDetails.status === 'in_progress') && (
                  <button
                    onClick={handleCompleteMaintenance}
                    style={styles.completeButton}
                  >
                    ✅ Complete Maintenance
                  </button>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
                No maintenance details available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign Maintenance Modal */}
      {showAssignMaintenanceModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Assign Maintenance - Room {selectedRoom?.room_number}</h2>
              <button style={styles.closeButton} onClick={closeModals}>×</button>
            </div>

            <form onSubmit={handleSubmitMaintenance}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Maintenance Type *</label>
                <select
                  style={styles.select}
                  value={maintenanceData.maintenanceType}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, maintenanceType: e.target.value })}
                  required
                >
                  <option value="repair">Repair</option>
                  <option value="renovation">Renovation</option>
                  <option value="deep_cleaning">Deep Cleaning</option>
                  <option value="inspection">Inspection</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description *</label>
                <textarea
                  style={styles.textarea}
                  value={maintenanceData.description}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, description: e.target.value })}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Priority</label>
                <select
                  style={styles.select}
                  value={maintenanceData.priority}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Start Date *</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={maintenanceData.startDate}
                    onChange={(e) => setMaintenanceData({ ...maintenanceData, startDate: e.target.value })}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Estimated End *</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={maintenanceData.estimatedEndDate}
                    onChange={(e) => setMaintenanceData({ ...maintenanceData, estimatedEndDate: e.target.value })}
                    min={maintenanceData.startDate}
                    required
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Duration (hours)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={maintenanceData.estimatedDuration}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, estimatedDuration: e.target.value })}
                  min="1"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Assigned To</label>
                <input
                  type="text"
                  style={styles.input}
                  value={maintenanceData.assignedTo}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, assignedTo: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Cost Estimate (₹)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={maintenanceData.costEstimate}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, costEstimate: e.target.value })}
                  min="0"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea
                  style={styles.textarea}
                  value={maintenanceData.notes}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, notes: e.target.value })}
                />
              </div>

              <button type="submit" style={styles.submitButton}>
                Schedule Maintenance
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Housekeeping Details Modal */}
      {showHousekeepingModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Room {selectedRoom?.room_number} - Housekeeping</h2>
              <button style={styles.closeButton} onClick={closeModals}>×</button>
            </div>

            {loadingDetails ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
                <div>Loading...</div>
              </div>
            ) : housekeepingDetails ? (
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                  Housekeeping Task
                </h3>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Task Type</span>
                  <span style={styles.detailValue}>{housekeepingDetails.task_type?.replace('_', ' ')}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Priority</span>
                  <span style={{
                    ...styles.priorityBadge,
                    background: getPriorityBg(housekeepingDetails.priority),
                    color: getPriorityColor(housekeepingDetails.priority)
                  }}>
                    {housekeepingDetails.priority}
                  </span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Status</span>
                  <span style={styles.detailValue}>{housekeepingDetails.status}</span>
                </div>

                {housekeepingDetails.notes && (
                  <div style={styles.sectionDivider}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                      Notes
                    </h3>
                    <p style={{ fontSize: '14px', color: colors.textDark }}>
                      {housekeepingDetails.notes}
                    </p>
                  </div>
                )}

                {housekeepingDetails.cleaned_by && (
                  <div style={styles.sectionDivider}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Cleaned By</span>
                      <span style={styles.detailValue}>{housekeepingDetails.cleaned_by}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Cleaned At</span>
                      <span style={styles.detailValue}>
                        {new Date(housekeepingDetails.cleaned_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                {housekeepingDetails.status === 'dirty' && (
                  <button
                    onClick={handleCompleteHousekeeping}
                    style={styles.completeButton}
                  >
                    ✅ Mark as Clean
                  </button>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
                No housekeeping task found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Extend Stay Modal */}
      {showExtendStayModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Extend Stay - Room {selectedRoom?.room_number}</h2>
              <button style={styles.closeButton} onClick={closeModals}>×</button>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Current Checkout Date</label>
              <div style={{ padding: '10px', background: colors.lightGray, borderRadius: '8px', fontWeight: '600' }}>
                {roomDetails?.room?.check_out_date ? new Date(roomDetails.room.check_out_date).toLocaleDateString() : 'N/A'}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>New Checkout Date</label>
              <input
                type="date"
                style={styles.input}
                value={extendStayData.newCheckOutDate}
                min={roomDetails?.room?.check_out_date ? new Date(new Date(roomDetails.room.check_out_date).getTime() + 86400000).toISOString().split('T')[0] : ''}
                onChange={handleExtendStayDateChange}
              />
            </div>

            {extendStayData.extraDays > 0 && (
              <div style={styles.sectionDivider}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: colors.primary }}>
                  Fare Calculation
                </h3>

                <div style={styles.detailRow}>
                  <span>Extra Days</span>
                  <span>{extendStayData.extraDays} day(s)</span>
                </div>
                <div style={styles.detailRow}>
                  <span>Price Per Night</span>
                  <span>₹{parseFloat(roomDetails?.room?.price_per_night || 0).toLocaleString('en-IN')}</span>
                </div>
                <div style={styles.detailRow}>
                  <span>Extended Stay Fare</span>
                  <span style={{ color: colors.primary, fontWeight: '700' }}>+ ₹{extendStayData.additionalAmount.toLocaleString('en-IN')}</span>
                </div>
                <div style={{ ...styles.detailRow, borderTop: '2px solid #eee', marginTop: '10px', paddingTop: '10px' }}>
                  <span>New Total Amount</span>
                  <span style={{ fontSize: '18px', fontWeight: '800' }}>₹{extendStayData.newTotalAmount.toLocaleString('en-IN')}</span>
                </div>

                <div style={{ ...styles.detailRow, color: colors.success }}>
                  <span>Already Paid</span>
                  <span>₹{parseFloat(roomDetails?.totalPaid || 0).toLocaleString('en-IN')}</span>
                </div>

                <div style={{ ...styles.detailRow, background: '#fee2e2', padding: '10px', borderRadius: '6px', marginTop: '10px' }}>
                  <span style={{ color: colors.danger, fontWeight: '700' }}>Net Payable Now</span>
                  <span style={{ color: colors.danger, fontWeight: '800', fontSize: '18px' }}>
                    ₹{Math.max(0, extendStayData.newTotalAmount - parseFloat(roomDetails?.totalPaid || 0)).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={handleConfirmExtension}
              disabled={extendingStay || extendStayData.extraDays <= 0}
              style={{ ...styles.submitButton, opacity: (extendingStay || extendStayData.extraDays <= 0) ? 0.7 : 1 }}
            >
              {extendingStay ? 'Processing...' : 'Confirm Extension & Update Bill'}
            </button>

          </div>
        </div>
      )}

      {/* Assign Housekeeping Modal */}
      {showAssignHousekeepingModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Assign Housekeeping - Room {selectedRoom?.room_number}</h2>
              <button style={styles.closeButton} onClick={closeModals}>×</button>
            </div>

            <form onSubmit={handleSubmitHousekeeping}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Task Type</label>
                <select
                  style={styles.select}
                  value={housekeepingData.taskType}
                  onChange={(e) => setHousekeepingData({ ...housekeepingData, taskType: e.target.value })}
                >
                  <option value="standard_cleaning">Standard Cleaning</option>
                  <option value="deep_cleaning">Deep Cleaning</option>
                  <option value="turndown_service">Turndown Service</option>
                  <option value="inspection">Inspection</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Priority</label>
                <select
                  style={styles.select}
                  value={housekeepingData.priority}
                  onChange={(e) => setHousekeepingData({ ...housekeepingData, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea
                  style={styles.textarea}
                  value={housekeepingData.notes}
                  onChange={(e) => setHousekeepingData({ ...housekeepingData, notes: e.target.value })}
                  placeholder="Any special instructions..."
                />
              </div>

              <button type="submit" style={styles.submitButton}>
                Assign Housekeeping Task
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Add Other Charge Modal */}
      {showAddChargeModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddChargeModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add Other Charge - Room {selectedRoom?.room_number}</h2>
              <button style={styles.closeButton} onClick={() => setShowAddChargeModal(false)}>×</button>
            </div>

            <form onSubmit={handleAddCharge}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Charge Type *</label>
                <select
                  style={styles.select}
                  value={newCharge.chargeType}
                  onChange={(e) => setNewCharge({ ...newCharge, chargeType: e.target.value })}
                  required
                >
                  <option value="">Select charge type</option>
                  <option value="laundry">Laundry</option>
                  <option value="damage">Damage / Broken Glass</option>
                  <option value="minibar">Minibar</option>
                  <option value="extra_service">Extra Service</option>
                  <option value="late_checkout_fee">Late Checkout Fee</option>
                  <option value="room_service">Room Service</option>
                  <option value="parking">Parking</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  style={styles.textarea}
                  value={newCharge.description}
                  onChange={(e) => setNewCharge({ ...newCharge, description: e.target.value })}
                  placeholder="Optional description..."
                  rows="3"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (₹) *</label>
                <input
                  type="number"
                  style={styles.input}
                  value={newCharge.amount}
                  onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <button type="submit" style={styles.submitButton}>
                Add Charge
              </button>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default FrontOfficeAvailability;